import { useQuery } from '@apollo/client';
import { OptimizerRunV2Mode } from '@generated/graphql';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { BULK_REBALANCE_BASE_PATH } from '../paths';
import { FETCH_OPTIMIZER_RUN_V2 } from '../queries/optimizerRunsV2.queries';
import type { BulkRebalanceRun } from '../types/run.types';
import { OPTIMIZER_POLL_INTERVAL_MS } from '../utils/detailPage/constants';
import {
  canShowRunReports,
  isRunInProgress,
  usesOptimizedPortfolioReportsQuery,
} from '../utils/presentation/runProgress';

export type UseBulkRebalanceDetailRunResult = {
  run: BulkRebalanceRun | null;
  runLoading: boolean;
  runErrorMessage?: string;
  inProgress: boolean;
  isUnsupportedDryRun: boolean;
  showReports: boolean;
  useOptimizedReportsQuery: boolean;
  succeededCount: number;
};

export const useBulkRebalanceDetailRun = (
  runId: string | undefined,
): UseBulkRebalanceDetailRunResult => {
  const navigate = useNavigate();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [isPolling, setIsPolling] = useState(true);

  const {
    loading: runLoading,
    error: runError,
    data: runData,
  } = useQuery(FETCH_OPTIMIZER_RUN_V2, {
    skip: !runId,
    fetchPolicy: 'no-cache',
    pollInterval: isPolling ? OPTIMIZER_POLL_INTERVAL_MS : 0,
    variables: {
      input: {
        optimizerRunId: runId,
      },
    },
    onError: (error) => {
      sendAnalytic(ovAnalyticsEvents.portfolioOptimizerDetailError, {
        optimizerRunId: runId,
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    },
  });

  const run: BulkRebalanceRun | null =
    runData?.fetchOptimizerRunV2?.optimizerRun ?? null;
  const inProgress = run ? isRunInProgress(run.state) : false;
  const isUnsupportedDryRun = run?.mode === OptimizerRunV2Mode.DryRun;
  const showReports =
    run && !isUnsupportedDryRun ? canShowRunReports(run.state) : false;
  const useOptimizedReportsQuery = run
    ? usesOptimizedPortfolioReportsQuery(run.state)
    : false;
  const succeededCount = run?.portfolioSummary?.succeeded ?? 0;

  useEffect(() => {
    setIsPolling(true);
  }, [runId]);

  useEffect(() => {
    if (!run || run.id !== runId) {
      return;
    }

    setIsPolling(isRunInProgress(run.state));
  }, [run, runId]);

  useEffect(() => {
    if (!runLoading && isUnsupportedDryRun) {
      navigate(BULK_REBALANCE_BASE_PATH, { replace: true });
    }
  }, [isUnsupportedDryRun, navigate, runLoading]);

  return {
    run,
    runLoading: runLoading && !run,
    runErrorMessage: runError?.message,
    inProgress,
    isUnsupportedDryRun,
    showReports,
    useOptimizedReportsQuery,
    succeededCount,
  };
};
